import { Github } from 'lucide-react';
import type { JSX } from 'react';
import { navItems, socialLinks } from '@/data/siteData';

const iconMap: Record<string, JSX.Element> = {
  github: <Github className="h-4 w-4" />,
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleScroll = (href: string) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="footer-surface border-t border-border bg-background">
      <div className="container-site py-10">
        <div className="flex flex-col md:flex-row justify-between gap-8">
          {/* Brand */}
          <div className="max-w-xs">
            <p className="font-sans uppercase font-extrabold text-sm tracking-wider text-foreground mb-2">Let&apos;s Make It Easy</p>
            <p className="text-muted-foreground text-xs leading-relaxed">
              AI automation made simple. Bikin teknologi kompleks jadi mudah dipahami.
            </p>
            <div className="flex gap-2 mt-4">
              {socialLinks.map((link) => (
                <a
                  key={link.platform}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.platform}
                  className="p-2 border border-border bg-card hover:bg-foreground hover:text-background transition-colors rounded-none"
                >
                  {iconMap[link.icon] ?? <span className="text-[10px] font-bold">{link.platform}</span>}
                </a>
              ))}
            </div>
          </div>

          {/* Nav */}
          <nav aria-label="Footer navigation">
            <p className="text-[10px] font-bold uppercase tracking-wider text-foreground mb-3">
              Sections
            </p>
            <ul className="grid grid-cols-2 gap-x-8 gap-y-2">
              {navItems.map((item) => (
                <li key={item.href}>
                  <button
                    type="button"
                    onClick={() => handleScroll(item.href)}
                    className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Subdomains / Projects */}
          <nav aria-label="Featured Sites">
            <p className="text-[10px] font-bold uppercase tracking-wider text-foreground mb-3">
              Subdomains
            </p>
            <ul className="flex flex-col gap-2">
              <li>
                <a
                  href="https://tk.letsmakeiteasy.work"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                >
                  tk.letsmakeiteasy.work
                </a>
              </li>
              <li>
                <a
                  href="https://falak.letsmakeiteasy.work"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                >
                  falak.letsmakeiteasy.work
                </a>
              </li>
              <li>
                <a
                  href="https://maps.letsmakeiteasy.work"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                >
                  maps.letsmakeiteasy.work
                </a>
              </li>
            </ul>
          </nav>
        </div>

        <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="text-[10px] uppercase font-bold text-muted-foreground">
            &copy; {currentYear} letsmakeiteasy.work. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="/privacy"
              className="text-[10px] uppercase font-bold text-muted-foreground hover:text-foreground transition-colors"
            >
              Privacy Policy
            </a>
            <a
              href="/terms"
              className="text-[10px] uppercase font-bold text-muted-foreground hover:text-foreground transition-colors"
            >
              Terms of Service
            </a>
            <button
              type="button"
              aria-label="Back to top"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="text-[10px] uppercase font-bold text-muted-foreground hover:text-foreground transition-colors"
            >
              Back to top <span aria-hidden="true">↑</span>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
